import { useEffect, useRef, useState } from 'react';

/** Live-typing Claude conversation. Mimics the actual MCP response shape
 * (tool name, structured result, follow-up). Runs on a single timeline
 * driven by useEffect; pauses while off-screen so it doesn't waste
 * cycles when the visitor is reading something else. */

type Step =
  | { kind: 'user'; text: string }
  | { kind: 'claude-pre'; text: string; tool: string }
  | { kind: 'card'; lines: { label: string; value: string; tone?: 'ok' | 'dim' | 'accent' }[] }
  | { kind: 'claude-post'; text: string }
  | { kind: 'pause'; ms: number };

const SCRIPT: Step[] = [
  {
    kind: 'user',
    text: 'Provision a new tenant for sendingdomain.com using the Smartlead key labelled "main".',
  },
  { kind: 'pause', ms: 500 },
  {
    kind: 'claude-pre',
    text: 'Calling create_tenant…',
    tool: 'create_tenant',
  },
  {
    kind: 'card',
    lines: [
      { label: '✓ tenant queued', value: '', tone: 'ok' },
      { label: 'id', value: '9a3f-1c2d-4a8b-9e23' },
      { label: 'domain', value: 'sendingdomain.com', tone: 'accent' },
      { label: 'sequencer', value: 'smartlead (main)' },
    ],
  },
  {
    kind: 'claude-post',
    text: 'Tenant is provisioning — Sendepot is handling everything end-to-end. Same-day delivery; up to 150 connected mailboxes typically within a few hours.',
  },
  { kind: 'pause', ms: 1800 },
  {
    kind: 'user',
    text: "What's still failing across my workspace?",
  },
  { kind: 'pause', ms: 500 },
  {
    kind: 'claude-pre',
    text: 'Calling queue_status, list_tenants…',
    tool: 'queue_status',
  },
  {
    kind: 'card',
    lines: [
      { label: 'failed', value: '4', tone: 'accent' },
      { label: 'running', value: '1' },
      { label: 'done', value: '68', tone: 'ok' },
    ],
  },
  {
    kind: 'claude-post',
    text: 'Four tenants in the failed bucket — all stuck in the OAuth phase. Want me to inspect each one and propose a fix?',
  },
  { kind: 'pause', ms: 4000 },
];

// Tunable typing speed (ms per character). Faster than literal typing —
// this is a stylized demo, not a tutorial.
const CHAR_MS = 18;
const PUNCT_MS = 60;          // brief lingering pause on . , ?
const POST_LINE_MS = 350;     // pause after a typed line completes

export default function AgentChat() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [progress, setProgress] = useState<{ stepIdx: number; chars: number }>({ stepIdx: -1, chars: 0 });
  // Rendered timeline — every step that has finished or is in progress.
  // Using indices keeps re-renders cheap.
  const [tick, setTick] = useState(0); // forces re-render on script reset

  // Run only when in view (cheap one-shot observer).
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => setVisible(e.isIntersecting)),
      { threshold: 0.25 },
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  // Sequenced typing engine. Each effect tick handles one tick of work
  // (one char advanced, or step completion). Cleaning up the timeout
  // makes pause-on-offscreen and reset both clean.
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;

    const totalChars = (s: Step) =>
      s.kind === 'user' || s.kind === 'claude-pre' || s.kind === 'claude-post' ? s.text.length : 0;

    function schedule(after: number, fn: () => void) {
      const id = setTimeout(() => { if (!cancelled) fn(); }, after);
      return id;
    }

    function advance(step: Step, idx: number, charIdx: number): number {
      // Schedule the next tick. Returns a timeout id (unused — we rely on
      // cancelled flag to stop).
      if (step.kind === 'pause') {
        schedule(step.ms, () => {
          setStepIndex(idx + 1);
          setProgress({ stepIdx: idx + 1, chars: 0 });
        });
        return 0;
      }
      if (step.kind === 'card') {
        // Cards render fully — animation handled by CSS slide-in.
        schedule(POST_LINE_MS, () => {
          setStepIndex(idx + 1);
          setProgress({ stepIdx: idx + 1, chars: 0 });
        });
        return 0;
      }
      // text-bearing step: advance one char at a time
      const total = totalChars(step);
      if (charIdx >= total) {
        // line done — pause briefly, then advance
        schedule(POST_LINE_MS, () => {
          setStepIndex(idx + 1);
          setProgress({ stepIdx: idx + 1, chars: 0 });
        });
        return 0;
      }
      const txt = (step as { text: string }).text;
      const nextChar = txt[charIdx];
      const delay = (nextChar === '.' || nextChar === ',' || nextChar === '?') ? PUNCT_MS : CHAR_MS;
      schedule(delay, () => {
        setProgress({ stepIdx: idx, chars: charIdx + 1 });
      });
      return 0;
    }

    if (stepIndex >= SCRIPT.length) {
      // Loop: short pause, then start over.
      schedule(1500, () => {
        setStepIndex(0);
        setProgress({ stepIdx: -1, chars: 0 });
        setTick(t => t + 1);
      });
      return () => { cancelled = true; };
    }

    const step = SCRIPT[stepIndex];
    if (progress.stepIdx !== stepIndex) {
      setProgress({ stepIdx: stepIndex, chars: 0 });
      return () => { cancelled = true; };
    }
    advance(step, stepIndex, progress.chars);
    return () => { cancelled = true; };
  }, [visible, stepIndex, progress, tick]);

  // Auto-scroll the conversation pane to the bottom as content streams
  // in. Without this, the panel stays a fixed height and new messages
  // would render below the visible area.
  const scrollerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [stepIndex, progress, tick]);

  // Build the rendered timeline up to (and including) the current step.
  const rendered: { idx: number; step: Step; charsTyped: number; complete: boolean }[] = [];
  for (let i = 0; i <= stepIndex && i < SCRIPT.length; i++) {
    const step = SCRIPT[i];
    if (step.kind === 'pause') continue;
    const isCurrent = i === stepIndex;
    const totalChars = step.kind === 'card' ? 0 : (step as { text: string }).text.length;
    const chars = isCurrent ? progress.chars : totalChars;
    rendered.push({
      idx: i, step, charsTyped: chars,
      complete: !isCurrent || chars >= totalChars,
    });
  }

  return (
    <div
      ref={ref}
      className="card overflow-hidden border-line2/60 shadow-[0_0_0_1px_rgba(139,92,246,0.18),0_24px_64px_-24px_rgba(139,92,246,0.25)]"
    >
      {/* Window chrome */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-line/60 bg-white/[0.02]">
        <span className="w-2 h-2 rounded-full bg-err/70" />
        <span className="w-2 h-2 rounded-full bg-warn/70" />
        <span className="w-2 h-2 rounded-full bg-ok/70" />
        <span className="text-[10px] font-mono text-dim ml-2">claude · sendepot mcp</span>
        <span className="ml-auto flex items-center gap-1.5 text-[10px] font-mono text-emerald-400/80">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          live
        </span>
      </div>

      {/* Fixed-height scroller so the panel never grows on the surrounding
          layout as more messages stream in. The newest content auto-
          scrolls into view via the effect above; the top fade implies
          earlier history is still there if you scroll. */}
      <div
        ref={scrollerRef}
        className="px-5 py-5 space-y-4 font-mono text-[12.5px] leading-relaxed h-[460px] overflow-y-auto relative
                   [mask-image:linear-gradient(to_bottom,transparent_0,black_28px,black_calc(100%-12px),transparent_100%)]"
      >
        {rendered.map(({ idx, step, charsTyped, complete }) => {
          const key = `${tick}-${idx}`;
          if (step.kind === 'user') {
            return (
              <Block key={key} who="You">
                <span className="text-ink">
                  {highlightTerms(step.text.slice(0, charsTyped))}
                  {!complete && <Caret />}
                </span>
              </Block>
            );
          }
          if (step.kind === 'claude-pre') {
            return (
              <Block key={key} who="Claude">
                <div className="text-ink">
                  {renderToolCall(step.text.slice(0, charsTyped))}
                  {!complete && <Caret />}
                </div>
              </Block>
            );
          }
          if (step.kind === 'card') {
            return (
              <div key={key} className="ml-9 -mt-2 animate-slide-up">
                <div className="rounded-md border border-line/60 bg-bg/40 px-3 py-2.5 inline-block min-w-[260px]">
                  {step.lines.map((l, li) => (
                    <div key={li} className={`flex items-baseline gap-2 text-[11px] ${li === 0 ? 'mb-1' : ''}`}>
                      <span className={
                        l.tone === 'ok'     ? 'text-emerald-400'
                      : l.tone === 'accent' ? 'text-accent'
                      :                       'text-dim'
                      }>{l.label}</span>
                      {l.value && <span className="text-ink/90">{l.value}</span>}
                    </div>
                  ))}
                </div>
              </div>
            );
          }
          if (step.kind === 'claude-post') {
            return (
              <Block key={key} who="Claude">
                <span className="text-ink">
                  {step.text.slice(0, charsTyped)}
                  {!complete && <Caret />}
                </span>
              </Block>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}

function Block({ who, children }: { who: 'You' | 'Claude'; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-dim mb-1">{who}</div>
      <div>{children}</div>
    </div>
  );
}

function Caret() {
  return <span className="inline-block w-[6px] h-[14px] -mb-[2px] ml-0.5 bg-accent/80 align-middle animate-pulse" />;
}

/** Highlight tool names + domains in the typed user prompt. We only do
 * this when the entire token is already typed so users don't see a
 * half-typed `sendingdomai` flash purple mid-keystroke. */
function highlightTerms(typed: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const tokens: { match: string; cls: string }[] = [
    { match: 'sendingdomain.com', cls: 'text-accent' },
    { match: '"main"', cls: 'text-emerald-400' },
  ];
  let s = typed;
  let key = 0;
  while (s.length > 0) {
    let hit: { i: number; t: { match: string; cls: string } } | null = null;
    for (const t of tokens) {
      const i = s.indexOf(t.match);
      if (i !== -1 && (!hit || i < hit.i)) hit = { i, t };
    }
    if (!hit) { parts.push(s); break; }
    if (hit.i > 0) parts.push(s.slice(0, hit.i));
    parts.push(<span key={key++} className={hit.t.cls}>{hit.t.match}</span>);
    s = s.slice(hit.i + hit.t.match.length);
  }
  return parts;
}

/** Color the tool-name token (e.g. `Calling create_tenant…`). */
function renderToolCall(typed: string): React.ReactNode {
  const m = typed.match(/^(Calling )(\w+)?(.*)$/s);
  if (!m) return typed;
  const [, prefix, tool, rest] = m;
  return (
    <>
      <span className="text-dim">{prefix}</span>
      <span className="text-run">{tool ?? ''}</span>
      <span className="text-dim">{rest ?? ''}</span>
    </>
  );
}
